import BarekamText from "../../../../pages/components/text";
import BarekamButton, { DialogButtons } from "../../../../pages/components/button";
import React, { useState } from "react";
import Message from "../../../../pages/components/message";
import { MessageSections } from "../../../../pages/components/message/message";
import {
    DialogActionsContainer,
    DialogButtonContainer
} from "../../../../pages/components/dialogs/standard-dialog/styled";
import { ActionDialogContent } from "./shared/styled";
import { Alert, Grid, ListItemText } from "@mui/material";
import BarekamInput from "../../../../pages/components/input";
import { getMessage } from "../../../../pages/shared/helper-functions";
import { OrderModel } from "../../../../pages/shared/models/order/order-item.model";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { BarekamSelect } from "../../../../pages/components/select/select";
import { BarekamMenuItem, SelectedBarekamMenuItem } from "../../../../pages/components/select/styled/orders";
import BarekamInputWithSelect from "../../../../pages/components/input-with-select";
import ordersService from "../../../../services/orders.service";
import { AxiosError } from "axios";
import { mapValidationErrors } from "../../../../pages/user/settings/personal-info/personal-info.service";
import { getProductStores } from "../../../../redux/slices/static-data.slice";

export const AnnounceOrder = ({ order, onCancel }: AnnounceOrderDialogProps) => {
    const [modifiedOrder, setModifiedOrder] = useState<OrderModel>(JSON.parse(JSON.stringify(order)));

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const { currencies, productStores, productCategories } = useAppSelector(state => state.staticDataReducer);
    const { messageCache, languageId } = useAppSelector(state => state.messagesReducer);
    const [filteredStores] = useState(productStores.filter(store => store.isOther || store.country?.countryId === order.shippingInfo?.country.countryId))

    const dispatch = useAppDispatch();

    function onChange(field: 'name' | 'price' | 'currency' | 'store' | 'storeName' | 'category', value: any) {
        setModifiedOrder(prevState => {
            switch (field) {
                case "name":
                    prevState.name = value;
                    prevState.products[0].name = value;
                    break;
                case "price":
                    prevState.products[0].price = value;
                    break;
                case "currency":
                    prevState.products[0].currency = value;
                    break;
                case "store":
                    prevState.products[0].store = value;
                    break;
                case "storeName":
                    prevState.products[0].storeName = value;
                    break;
                case "category":
                    prevState.products[0].category = value;
                    break;
            }

            prevState.products[0] = { ...prevState.products[0] }
            return { ...prevState };
        })

    }

    function passedValidations() {
        const errors = {} as { [key: string]: string };
        const product = modifiedOrder.products[0];

        if (!modifiedOrder.name) {
            errors['order_name'] = 'required';
        }
        if (!product.category) {
            errors['category'] = 'required';
        }
        if (!product.price) {
            errors['price'] = 'required';
        }
        if (!product.store) {
            errors['store'] = 'required';
        }

        setErrors(errors);

        return !Object.keys(errors).length;
    }

    function handleSubmit() {
        if (passedValidations()) {
            const updateData = parseUpdateDataForAnnouncing(modifiedOrder);

            if (order.orderId) {
                ordersService.announceOrder(order.orderId, updateData)
                    .then(() => dispatch(getProductStores({})))
                    .then(() => onCancel())
                    .catch(e => {
                        if (e instanceof AxiosError) {
                            setErrors(mapValidationErrors(e));
                        }
                    });
            }
        }
    }

    return (
        <>
            <ActionDialogContent>
                <Grid item xs={12}>
                    <Alert severity="info">
                        <BarekamText variant={"m"}>
                            <Message messageKey={'announce_order_info'} sectionId={MessageSections.ORDERS} />
                        </BarekamText>
                    </Alert>
                </Grid>

                <Grid item xs={12}>
                    <BarekamInput value={modifiedOrder.name}
                        placeholder={getMessage('order_name', MessageSections.ORDERS, messageCache, languageId)}
                        customInputVariant={"standard"}
                        width={'100%'}
                        onChange={(event) => onChange('name', event.target.value)}
                        error={!!errors['order_name']}
                        errorMessage={errors['order_name'] && getMessage(errors['order_name'], MessageSections.ORDERS, messageCache, languageId)}
                        required />
                </Grid>

                <Grid item xs={12}>
                    <BarekamSelect
                        variant={'outlined'}
                        label={<BarekamText variant={'1rem'}>
                            <Message messageKey={'product_category'} sectionId={MessageSections.ORDERS} />
                        </BarekamText>}
                        value={modifiedOrder.products[0]?.category && modifiedOrder.products[0]?.category?.productCategoryId || ''}
                        type={"standard"}
                        error={!!errors['category']}
                        errorMessage={errors['category'] && getMessage(errors['category'], MessageSections.ORDERS, messageCache, languageId)}
                        fullWidth
                        required>
                        {
                            productCategories.map(category => {
                                if (modifiedOrder.products[0]?.category && (modifiedOrder.products[0]?.category.productCategoryId === category.productCategoryId)) {
                                    return (
                                        <SelectedBarekamMenuItem key={category.productCategoryId}
                                            value={category.productCategoryId}
                                            onClick={() => onChange('category', category)}>
                                            <ListItemText style={{ margin: 'unset' }}
                                                primary={category.name[languageId]}
                                                secondary={category.description[languageId]}
                                            />
                                        </SelectedBarekamMenuItem>
                                    )
                                } else {
                                    return (
                                        <BarekamMenuItem key={category.productCategoryId}
                                            value={category.productCategoryId}
                                            onClick={() => onChange('category', category)}>
                                            <ListItemText style={{ margin: 'unset' }}
                                                primary={category.name[languageId]}
                                                secondary={category.description[languageId]}
                                            />
                                        </BarekamMenuItem>
                                    );
                                }
                            })
                        }
                    </BarekamSelect>
                </Grid>

                <Grid item xs={12}>
                    <BarekamInputWithSelect types={currencies}
                        selectedType={modifiedOrder.products[0].currency?.currencyId || 1}
                        selectedValue={modifiedOrder.products[0].price}
                        onValueChange={(value) => onChange('price', value)}
                        onTypeChange={(value) => onChange('currency', value)}
                        idFieldName={'currencyId'}
                        error={!!errors['price']}
                        errorMessage={errors['price'] && getMessage(errors['price'], MessageSections.ORDERS, messageCache, languageId)}
                        placeholderKey={'price'}
                        required />
                </Grid>

                <Grid item xs={12}>
                    <BarekamSelect
                        variant={'outlined'}
                        label={<BarekamText variant={'1rem'}>
                            <Message messageKey={'product_store'} sectionId={MessageSections.ORDERS} />
                        </BarekamText>}
                        value={modifiedOrder.products[0]?.store && modifiedOrder.products[0]?.store?.storeId || ''}
                        type={"standard"}
                        error={!!errors['store']}
                        errorMessage={errors['store'] && getMessage(errors['store'], MessageSections.ORDERS, messageCache, languageId)}
                        fullWidth
                        required>
                        {
                            filteredStores.map(store => {
                                if (modifiedOrder.products[0]?.store && (modifiedOrder.products[0]?.store?.storeId === store.storeId)) {
                                    return (
                                        <SelectedBarekamMenuItem key={store.storeId}
                                            value={store.storeId}>
                                            <ListItemText style={{ margin: 'unset' }}
                                                primary={store.name && store.name[languageId]}
                                            />
                                        </SelectedBarekamMenuItem>
                                    )
                                } else {
                                    return (
                                        <BarekamMenuItem key={store.storeId}
                                            value={store.storeId}
                                            onClick={() => onChange('store', store)}>
                                            <ListItemText style={{ margin: 'unset' }}
                                                primary={store.name && store.name[languageId]}
                                            />
                                        </BarekamMenuItem>
                                    );
                                }
                            })
                        }
                    </BarekamSelect>
                </Grid>

                {modifiedOrder.products[0].store?.isOther && (
                    <Grid item xs={12}>
                        <BarekamInput value={modifiedOrder.products[0].storeName}
                            placeholder={getMessage('store', MessageSections.ORDERS, messageCache, languageId)}
                            customInputVariant={"standard"}
                            width={'100%'}
                            error={!!errors['storeName']}
                            errorMessage={errors['storeName'] && getMessage(errors['storeName'], MessageSections.ORDERS, messageCache, languageId)}
                            onChange={(event) => onChange('storeName', event.target.value)}
                            required />
                    </Grid>
                )}
            </ActionDialogContent>

            <DialogActionsContainer>
                <DialogButtonContainer>
                    <BarekamButton type={DialogButtons.CANCEL} onClick={() => onCancel()}>
                        <BarekamText variant={'s'} fontWeight={500} align={"center"}>
                            <Message messageKey={'cancel'} sectionId={MessageSections.MAIN} />
                        </BarekamText>
                    </BarekamButton>

                    <BarekamButton type={DialogButtons.SAVE} onClick={() => handleSubmit()}>
                        <BarekamText variant={'s'} fontWeight={700} align={"center"}>
                            <Message messageKey={'submit'} sectionId={MessageSections.ORDERS} />
                        </BarekamText>
                    </BarekamButton>
                </DialogButtonContainer>
            </DialogActionsContainer>
        </>
    )
}

export function parseUpdateDataForAnnouncing(order: OrderModel) {
    const serializedProducts = order.products.map(product => (
        {
            productId: product.productId,
            name: product.name,
            price: product.price,
            currency: product.currency?.currencyId || 1,
            store: product.store?.storeId || null,
            storeName: product.storeName,
            category: product.category?.productCategoryId || null,
        }
    ));

    const serializedShippingInfo = {
        country: order.shippingInfo.country.countryId,
        warehouse: order.shippingInfo.warehouse.warehouseId,
        destinationWarehouse: order.shippingInfo.destinationWarehouse?.warehouseId,
        homeDeliveryAddress: order.shippingInfo.homeDeliveryAddress?.addressId,
        shippingMethod: order.shippingInfo.shippingMethod.shippingMethodId
    }

    return {
        name: order.name,
        products: serializedProducts,
        shippingInfo: serializedShippingInfo
    }
}

interface AnnounceOrderDialogProps {
    order: OrderModel,
    onCancel: () => void,
}
